/* Semantic UI helpers */
import * as _ from 'lodash';
import * as React from 'react';
import {DropdownItemProps, Icon} from 'semantic-ui-react';

import LegacyWBIcon from '../components/elements/LegacyWBIcon';
import {RequireSome} from '../types/base';
import {fuzzyMatchHighlight, fuzzyMatchWithMapping} from '../util/fuzzyMatch';
import * as Run from '../util/runs';

const KEY_LIMIT = 100;

export const UI_TAG_TEXT = 'tag';

export interface DropdownOption {
  key: string;
  text: string;
  value: string;
}

export type Option = RequireSome<DropdownItemProps, 'value' | 'text'> & {
  content?: DropdownItemProps['content'];
  key?: DropdownItemProps['key'];
};

export const maybePluralize = (count: number, noun: string, suffix = 's') =>
  `${count} ${noun}${count !== 1 ? suffix : ''}`;

export const maybePluralizeWord = (count: number, noun: string, suffix = 's') =>
  `${noun}${count !== 1 ? suffix : ''}`;

// Gets the display text for based of a dropdown prop based on key
export const getText = (key: Run.Key) => {
  if (_.isEqual(key, Run.GROUP_BY_ALL_KEY)) {
    return 'All';
  }
  if (key.section === 'tags') {
    return UI_TAG_TEXT;
  }
  if (key.section === 'run') {
    if (key.name === 'name') {
      return 'id';
    } else if (key.name === 'displayName') {
      return 'name';
    } else if (key.name === 'group') {
      return 'Group';
    } else if (key.name === 'jobType') {
      return 'Job Type';
    } else if (key.name === 'username' || key.name === 'userName') {
      return 'Username';
    } else if (key.name === 'createdAt') {
      return 'Date created';
    } else if (key.name === 'host') {
      return 'Hostname';
    } else if (key.name === 'runInfo.gpu') {
      return 'GPU Type';
    } else if (key.name === 'runInfo.gpuCount') {
      return 'GPU Count';
    }
  } else if (key.section === 'config') {
    return key.name.replace('.value', '');
  } else if (key.section === 'summary') {
    if (key.name === '_runtime') {
      return 'Relative Time (Process)';
    } else if (key.name === '_timestamp') {
      return 'Wall Time';
    } else if (key.name === '_step') {
      return 'Step';
    }
  }
  return key.name;
};

// Take a dropdown option and beautifies it.
// Adding an image and casting the text
export function beautify(item: Option): Option {
  const key = Run.keyFromString(item.key);
  const image = key ? getImage(key) : <Icon name="tasks" />;
  const text = key ? getText(key) : item.key;

  return {
    ...item,
    text: image ? (
      <span className="dropdown-with-icon">
        {image} {text}
      </span>
    ) : (
      text
    ),
  };
}

export const searchFunction = (options: DropdownItemProps[], query: string) => {
  const matched = fuzzyMatchWithMapping(options, query, o =>
    o.key.toString()
  ).slice(0, KEY_LIMIT);
  return matched.map(o => {
    const key = Run.keyFromString(o.key);
    return beautify({
      key,
      value: key ?? o.key,
      ...o,
      text: key != null ? fuzzyMatchHighlight(getText(key), query) : o.key,
    });
  });
};

export const getImage = (key: Run.Key) => {
  return <LegacyWBIcon name={getIconNameForKey(key)} />;
};

export const getIconNameForKey = (key: Run.Key) => {
  if (key.section === 'run') {
    switch (key.name) {
      case 'createdAt':
        return 'date';
      case 'duration':
        return 'duration';
      case 'name':
        return 'metadata';
      case 'status':
        return 'state';
      case 'username':
        return 'username';
      default:
        return 'metadata';
    }
  }

  switch (key.section) {
    case 'config':
      return 'configuration';
    case 'summary':
      return 'summary';
    case 'tags':
      return 'tag-gray';
    default:
      return 'summary';
  }
};

export const isTag = (v: string) => {
  return v.split(':')[0] === 'tags';
};

export function partitionTags(values: string[]): [string[], string[]] {
  return _.partition<string>(values, isTag);
}

export function tagOption(v: string): Option {
  return beautify({
    text: UI_TAG_TEXT,
    key: v,
    value: v,
  });
}

export function makeOptions(values: string[]) {
  return values.map(v => ({text: v, key: v, value: v}));
}

export function makeOptionsFromKeyStrings(values: string[]) {
  return makeOptions(values).map(option => ({
    ...option,
    text: Run.keyStringDisplayName(option.text),
  }));
}

// e.persist() is necessary to use setTimeout in an event handler (like with _.debounce or _.throttle)
// see Event Pooling here: https://reactjs.org/docs/events.html
export function handleAsyncEvent(
  e: React.ChangeEvent<HTMLInputElement>,
  // tslint:disable-next-line:ban-types
  eventHandler: Function
) {
  e.persist();
  eventHandler(e);
}
